import React, { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import moment from 'moment';

const BRAND = '#E53E14';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Calendar-style month + year picker.
 *
 * Replaces a flat list of every selectable month, which worked at thirteen
 * options and would not at sixty: the year is chosen with the arrows and the
 * month from a grid, so the list never grows.
 *
 * `value`, `minMonth` and `maxMonth` are all `YYYY-MM`. Months outside
 * [minMonth, maxMonth] render disabled rather than being hidden, so a reader can
 * see that a month exists and is simply not applicable to this company — hiding
 * them makes an out-of-range year look empty and broken.
 *
 * NOTE: this project loads only `@tailwind utilities` (see src/index.css) — there
 * is NO preflight, so every bare <button> keeps Chrome's default outset border
 * and grey button face. That is why `border-0 appearance-none bg-transparent` is
 * spelled out on each one; drop them and the grid renders as a block of grey
 * system buttons.
 */
const MonthYearPicker = ({ value, onChange, minMonth, maxMonth, label = 'Month' }) => {
    const selected = moment(`${value}-01`, 'YYYY-MM-DD');
    const min = moment(`${minMonth}-01`, 'YYYY-MM-DD');
    const max = moment(`${maxMonth}-01`, 'YYYY-MM-DD');

    // The year on display, which is not necessarily the selected one — the user
    // can page through years without committing to a month.
    const [year, setYear] = useState(selected.year());

    // Re-open on the selected year rather than wherever the user last browsed.
    useEffect(() => { setYear(selected.year()); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const minYear = min.year();
    const maxYear = max.year();

    const monthKey = (m) => `${year}-${String(m + 1).padStart(2, '0')}`;
    const isDisabled = (m) => {
        const k = moment(`${monthKey(m)}-01`, 'YYYY-MM-DD');
        return k.isBefore(min, 'month') || k.isAfter(max, 'month');
    };

    return (
        <Popover className="relative">
            {() => (
                <>
                    <Popover.Button className="flex items-center gap-2 rounded border-0 bg-gray-100 px-3 py-2 text-sm text-gray-900 shadow appearance-none cursor-pointer focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-500">
                            <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5a1.25 1.25 0 00-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5A1.25 1.25 0 0015.25 7.5H4.75z" clipRule="evenodd" />
                        </svg>
                        <span>{selected.format('MMMM YYYY')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                            <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clipRule="evenodd" />
                        </svg>
                    </Popover.Button>

                        {/* No <Transition> wrapper. Wrapping Popover.Panel in one kept the
                            panel mounted regardless of the Popover's own state here: neither
                            `close()` from the render prop nor a Popover.Button inside the panel
                            would dismiss it, so the calendar stayed open over the card the user
                            had just filtered. Letting Panel mount and unmount itself fixes it;
                            the fade was not worth a control that would not close. */}
                        <Popover.Panel className="absolute right-0 z-20 mt-2 w-64 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5">
                            {/* Year row */}
                            <div className="flex items-center justify-between pb-2">
                                <button
                                    type="button"
                                    aria-label="Previous year"
                                    disabled={year <= minYear}
                                    onClick={() => setYear((y) => y - 1)}
                                    className="rounded border-0 bg-transparent px-2 py-1 text-gray-600 appearance-none cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                                >
                                    &larr;
                                </button>
                                <span className="text-sm font-semibold text-gray-900 tabular-nums">{year}</span>
                                <button
                                    type="button"
                                    aria-label="Next year"
                                    disabled={year >= maxYear}
                                    onClick={() => setYear((y) => y + 1)}
                                    className="rounded border-0 bg-transparent px-2 py-1 text-gray-600 appearance-none cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                                >
                                    &rarr;
                                </button>
                            </div>

                            {/* Month grid */}
                            <div className="grid grid-cols-3 gap-1">
                                {MONTHS.map((name, m) => {
                                    const key = monthKey(m);
                                    const disabled = isDisabled(m);
                                    const isSelected = key === value;
                                    const isThisMonth = key === moment().format('YYYY-MM');
                                    return (
                                        <Popover.Button
                                            as="button"
                                            key={name}
                                            type="button"
                                            disabled={disabled}
                                            title={disabled ? 'Outside this company’s reporting range' : undefined}
                                            /* close() BEFORE onChange: onChange re-renders the
                                               parent (and therefore this component), and the
                                               close was being lost in that cascade -- the panel
                                               stayed open over the card the user had just
                                               filtered. */
                                            /* A Popover.Button inside the panel closes it --
                                               that is Headless UI's own mechanism, and it works
                                               where calling close() from the render prop did not
                                               (the panel stayed open over the card the user had
                                               just filtered). */
                                            onClick={() => onChange(key)}
                                            className="rounded border-0 py-2 text-sm appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                                            style={
                                                isSelected
                                                    ? { backgroundColor: BRAND, color: '#fff', fontWeight: 600 }
                                                    : {
                                                        backgroundColor: 'transparent',
                                                        // The month in progress is marked, not selected --
                                                        // it is a hint about where "now" is, not a state.
                                                        color: isThisMonth ? BRAND : '#374151',
                                                    }
                                            }
                                        >
                                            {name}
                                        </Popover.Button>
                                    );
                                })}
                            </div>

                            <p className="mt-2 border-0 border-t border-solid border-gray-100 pt-2 text-[11px] text-gray-500">
                                {label} range: {min.format('MMM YYYY')} &ndash; {max.format('MMM YYYY')}
                            </p>
                        </Popover.Panel>
                </>
            )}
        </Popover>
    );
};

export default MonthYearPicker;
